import type { FC } from 'react'

import { LoaderWrapper } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'

import { BreadCrumbs } from '@/modules/admin/drilldown/BreadCrumbs'
import { DrillDownOptions } from '@/modules/admin/drilldown/DrillDownOptions'
import { CustomerHead } from '@/modules/admin/drilldown/org-chart/CustomerHead'
import { FrameAgreement } from '@/modules/admin/drilldown/org-chart/FrameAgreement'
import queries from '@/modules/admin/queries'
import type { DrillDownParams } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

import { useDrillDownContext } from '../context/index.context'
import type { FrameAgreementData } from '../types'
import { formatOrgData } from '../utils'

import './OrgChart.styles.scss'

export const OrgChart: FC = () => {
  const { state } = useDrillDownContext()

  const params: DrillDownParams = {
    year: String(state.year),
    period: String(state.period),
  }

  const { data: customerList = [], isLoading } = useQuery(
    queries.adminDrillDownCustomerTotals(params),
    () => adminResources.getDrillDownCustomerTotals(params)
  )

  if (customerList.length <= 0) {
    return <div>No data...</div>
  }

  const orgData = formatOrgData(customerList)

  return (
    <LoaderWrapper isLoading={isLoading}>
      <BreadCrumbs />
      <DrillDownOptions
        data={customerList.map(item => ({
          'Frame agreement': item.customerHeadFrameAgreementName,
          'Customer head': item.customerHeadName,
          Customer: item.customerName,
          'Total amount': item.amount,
          'Total salary deduction amount': item.salaryDeductionAmount,
        }))}
        title="Report"
        currencies={[]}
      />
      <div className="org-chart">
        <RootNode data={orgData} />
      </div>
    </LoaderWrapper>
  )
}

const RootNode: FC<{ data: FrameAgreementData }> = ({ data }) => {
  const hasMultipleRecordsWithSameCustomerHeadId = data.customerHeads.size > 1

  if (hasMultipleRecordsWithSameCustomerHeadId) {
    return <FrameAgreement customerList={data} />
  }

  const [firstEntry] = data.customerHeads.entries()

  return <CustomerHead customerHead={firstEntry[1]} isRootNode={true} />
}
